import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { Actions, ofType } from '@ngrx/effects';
import { createSelector, Store } from '@ngrx/store';
import { filter, firstValueFrom, map, type Observable, race, shareReplay, take } from 'rxjs';

import { filterBoolean } from '@core/common-legacy';
import { createActionStream } from '@core/ngrx-legacy';
import {
  resolveMemberStatus,
  TellentCurrentUserEntityActions,
  TellentCurrentUserSelectors,
  TellentMembersActions,
  TellentMembersSelectors,
} from '@core/tellent-data';
import {
  type OrgFeatures,
  SeatKind,
  TeamMembersStatuses,
  TeamMemberStatus,
  TellentCurrentUser,
  TellentCurrentUserUpdate,
  TellentOrgAbilities,
  TellentPasswordChange,
} from '@core/tellent-types';

@Injectable({ providedIn: 'root' })
export class TellentCurrentUserDataFacade {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);

  public currentUser = this.store.selectSignal<TellentCurrentUser | null>(
    TellentCurrentUserSelectors.data,
  );
  public currentMember = this.store.selectSignal(
    TellentCurrentUserSelectors.currentOrganizationMembershipSelectorSafe,
  );
  public currentOrg = computed(() => {
    const member = this.currentMember();
    return member ? member.org : null;
  });
  public currentUser$ = this.store
    .select<TellentCurrentUser | null>(TellentCurrentUserSelectors.data)
    .pipe(filterBoolean, shareReplay({ bufferSize: 1, refCount: true }));
  public organizationMembership = this.store.selectSignal(
    TellentCurrentUserSelectors.currentOrganizationMembershipSelector,
  );
  public organizationMembershipSafe = this.store.selectSignal(
    TellentCurrentUserSelectors.currentOrganizationMembershipSelectorSafe,
  );
  public hasFullAccess = this.store.selectSignal<boolean | null>(
    TellentCurrentUserSelectors.hasFullAccess,
  );
  public hasOtherFullAccessMembersInCurrentOrg = this.store.selectSignal(
    createSelector(
      TellentCurrentUserSelectors.data,
      TellentMembersSelectors.allEntities,
      (user, members) => {
        if (!user) return false;
        // The /members fetch is already scoped to the current org and does not
        // serialize org_guid, so we cannot (and need not) match on orgGuid here.
        // Only fully active members (accepted invitation, linked account) count —
        // a pending invite cannot yet act as an owner.
        return members.some(
          m =>
            m.userGuid !== user.guid &&
            resolveMemberStatus(m) === TeamMemberStatus.Active &&
            m.abilities.includes(TellentOrgAbilities.ManageTellent),
        );
      },
    ),
  );
  public anyOrgFullAccess$ = this.store
    .select(TellentCurrentUserSelectors.anyOrgFullAccess)
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));
  public members = toSignal(
    this.store.select(TellentCurrentUserSelectors.data).pipe(
      filterBoolean,
      map(({ members }) =>
        members.map(m => ({
          ...m,
          hasFullAccess: m.abilities.includes(TellentOrgAbilities.ManageTellent),
        })),
      ),
    ),
  );
  public currentUserGuid = computed(() => this.currentUser()?.guid);

  public fetchFailed$ = createActionStream(TellentCurrentUserEntityActions.fetchFailed);
  public updateUserSuccess$ = createActionStream(
    TellentCurrentUserEntityActions.updateCurrentUserDataSuccess,
  );
  public updateUserFail$ = createActionStream(
    TellentCurrentUserEntityActions.updateCurrentUserDataFail,
  );
  public updatePasswordSuccess$ = createActionStream(
    TellentCurrentUserEntityActions.updatePasswordSuccess,
  );
  public updatePasswordFail$ = createActionStream(
    TellentCurrentUserEntityActions.updatePasswordFail,
  );

  public seatByKind(kind: SeatKind) {
    return this.store.selectSignal(TellentCurrentUserSelectors.seatByKindSelector(kind));
  }

  public hasSeatAbility(kind: SeatKind, ability: string) {
    const seat = this.seatByKind(kind);
    return computed(() => {
      const abilities = seat()?.role?.abilities ?? [];
      return abilities.some(value => ability === value);
    });
  }

  public fetch() {
    this.store.dispatch(TellentCurrentUserEntityActions.fetch());
  }

  public waitForMembership(orgGuid: string): Observable<'ready' | 'failed'> {
    const ready$ = this.store.select(TellentCurrentUserSelectors.data).pipe(
      filter(user => !!user?.members.some(m => m.orgGuid === orgGuid)),
      take(1),
      map(() => <const>'ready'),
    );
    const failed$ = this.fetchFailed$.pipe(
      take(1),
      map(() => <const>'failed'),
    );
    return race(ready$, failed$);
  }

  public leaveCompany(orgGuid: string) {
    this.store.dispatch(TellentCurrentUserEntityActions.leaveCompany({ payload: { orgGuid } }));
  }

  public async isLastFullAccessMemberInCurrentOrg(): Promise<boolean> {
    if (this.hasFullAccess() !== true) {
      return false;
    }

    await this.ensureActiveMembersLoaded();

    return !this.hasOtherFullAccessMembersInCurrentOrg();
  }

  public deleteAccount(emailCode: string) {
    this.store.dispatch(
      TellentCurrentUserEntityActions.deleteAccount({ payload: { code: emailCode } }),
    );
  }

  public updateUser(payload: Partial<TellentCurrentUserUpdate>) {
    this.store.dispatch(TellentCurrentUserEntityActions.updateCurrentUserData({ payload }));
  }

  public updatePassword(payload: TellentPasswordChange) {
    this.store.dispatch(
      TellentCurrentUserEntityActions.updatePassword({
        payload,
      }),
    );
  }

  public getOrgFeature(feature: OrgFeatures) {
    return computed(() => {
      const features = this.currentOrg()?.features ?? [];
      return features.some(f => f === feature);
    });
  }

  private ensureActiveMembersLoaded(): Promise<unknown> {
    const loaded = firstValueFrom(
      this.actions$.pipe(
        ofType(
          TellentMembersActions.fetchPaginatedCollectionSuccess,
          TellentMembersActions.fetchPaginatedCollectionFailed,
        ),
        take(1),
      ),
    );

    this.store.dispatch(
      TellentMembersActions.fetchPaginatedCollection({
        payload: {
          params: {
            scope: TeamMembersStatuses.Active,
            statuses: [TeamMembersStatuses.Active],
          },
        },
      }),
    );

    return loaded;
  }
}
